import { useMemo, useState } from "react";
import { memoryStore } from "../utils/storage";
import { TYPE_META } from "./ActivityLogPanel";
import QuickLogModal from "./QuickLogModal";
import AgentProfileModal from "./AgentProfileModal";
import SettingsScreen from "./SettingsScreen";
import { useIsMobile } from "../hooks/useIsMobile";

const TODAY = new Date().toISOString().slice(0, 10);

function dateDiffDays(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.floor((d - now) / 86400000);
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.floor((now - d) / 86400000);
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("he-IL", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  });
}

const todayLabel = new Date().toLocaleDateString("he-IL", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
});

const SOURCE_FILTERS = [
  { key: "all", label: "הכל" },
  { key: "advice", label: "📋 טרום-ייעוץ" },
  { key: "holdings", label: "📦 אחזקות" },
  { key: "retirement", label: "📐 פרישה" },
];

export default function HomeDashboard({
  clients,
  sourceFilter = "all",
  onSourceFilterChange,
  logEntries,
  onSelectClient,
  onAddNew,
  onOpenLog,
  onUpdateLog,
  onLogActivity,
  onDeleteLog,
  onOpenFilesManager,
  onBulkSend,
}) {
  // ── Clients with plans ──────────────────────────────────────
  const clientsWithPlans = useMemo(() => {
    try {
      const raw = memoryStore.getItem("pension_plans_v1");
      const plans = raw ? JSON.parse(raw) : {};
      return new Set(
        Object.entries(plans)
          .filter(([, v]) => Array.isArray(v) && v.length > 0)
          .map(([clientId]) => clientId)
      );
    } catch { return new Set(); }
  }, [clients]);

  // ── Family relationships per client ─────────────────────────
  const allRelations = useMemo(() => {
    try {
      const raw = memoryStore.getItem("pension_relationships_v1");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  }, [clients]);

  // ── "Requires attention" items ──────────────────────────────
  const urgentItems = useMemo(() => {
    const tasks = logEntries
      .filter(e => e.type === "task" && !e.done)
      .map(e => ({ ...e, _kind: "task" }));
    const fees = logEntries
      .filter(e => e.type === "fee" && !e.feeCollected)
      .map(e => ({ ...e, _kind: "fee" }));
    return [...tasks, ...fees].sort((a, b) => {
      const da = a.dueDate || a.date || "9999";
      const db = b.dueDate || b.date || "9999";
      return da < db ? -1 : 1;
    });
  }, [logEntries]);

  // ── Per-client task counts & last activity ──────────────────
  const clientMeta = useMemo(() => {
    const meta = {};
    for (const e of logEntries) {
      if (!e.clientName) continue;
      if (!meta[e.clientName]) meta[e.clientName] = { openTasks: 0, lastEntry: null, unpaidFees: 0 };
      if (e.type === "task" && !e.done) meta[e.clientName].openTasks++;
      if (e.type === "fee" && !e.feeCollected) meta[e.clientName].unpaidFees++;
      if (!meta[e.clientName].lastEntry) meta[e.clientName].lastEntry = e;
    }
    return meta;
  }, [logEntries]);

  // ── Recent activity ─────────────────────────────────────────
  const recentActivity = useMemo(
    () => logEntries.filter(e => !TYPE_META[e.type]?.auto),
    [logEntries]
  );

  // ── Search + quick log ──────────────────────────────────────
  const [search, setSearch] = useState("");
  const [quickLogClient, setQuickLogClient] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [showAgentProfile, setShowAgentProfile] = useState(false);
  const [view, setView] = useState("home"); // "home" | "settings"
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      c.fileInfo?.client?.toLowerCase().includes(q) ||
      c.fileInfo?.clientId?.toLowerCase().includes(q)
    );
  }, [clients, search]);
  const activeProcessClients = useMemo(() => {
    return clients.filter(c => clientsWithPlans.has(c.clientId));
  }, [clients, clientsWithPlans]);

  const hasClients = clients.length > 0;
  // "דורש טיפול" section — rendered in two spots: above everything on desktop,
  // inside the B/C flex column on mobile (order 2, between clients and recent
  // activity). Defined once here so both spots share the same JSX.
  const urgentSection = (style) => urgentItems.length > 0 && (
    <section style={style}>
      <SectionTitle>🔴 דורש טיפול ({urgentItems.length})</SectionTitle>
      <div style={{
        background: "white", borderRadius: 12,
        border: "1px solid #fecaca", overflow: "hidden",
      }}>
        {urgentItems.map(item => {
          const diff = dateDiffDays(item.dueDate || item.date);
          const overdue = item._kind === "task" && diff !== null && diff < 0;
          const urgent  = item._kind === "task" && diff !== null && diff <= 1 && diff >= 0;
          const rowBg = overdue ? "#fff5f5" : urgent ? "#fffbeb" : "white";
          const accentColor = overdue ? "#dc2626" : urgent ? "#d97706" : "#64748b";

          return (
            <div key={item.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "11px 16px", borderBottom: "1px solid #f1f5f9",
              background: rowBg,
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{TYPE_META[item.type]?.icon || "📌"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                  {item.description}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, display: "flex", gap: 8 }}>
                  {item.clientName && <span>👤 {item.clientName}</span>}
                  {item._kind === "fee" && item.amount > 0 && (
                    <span style={{ color: "#d97706", fontWeight: 700 }}>
                      {Number(item.amount).toLocaleString("he-IL")} ₪
                    </span>
                  )}
                  {item._kind === "task" && item.dueDate && (
                    <span style={{ color: accentColor, fontWeight: 600 }}>
                      {overdue ? `באיחור ${Math.abs(diff)} ימים` : diff === 0 ? "היום" : diff === 1 ? "מחר" : fmtDate(item.dueDate)}
                    </span>
                  )}
                </div>
              </div>
              {item._kind === "task" && (
                <div style={{ display: "flex", gap: 6 }}>
                  <ActionItemButton
                    onClick={() => setEditEntry(item)}
                    text="ערוך"
                    defaultIcon="✏️"
                    hoverBg="#e0f2fe"
                    hoverColor="#0284c7"
                    hoverIcon="✏️"
                  />
                  <ActionItemButton
                    onClick={() => onUpdateLog(item.id, { done: true })}
                    text="סמן כבוצע"
                    defaultIcon="○"
                    hoverBg="#dcfce7"
                    hoverColor="#166534"
                    hoverIcon="✓"
                  />
                </div>
              )}
              {item._kind === "fee" && (
                <ActionItemButton
                  onClick={() => onUpdateLog(item.id, { feeCollected: true })}
                  text="סמן כשולם"
                  defaultIcon="○"
                  hoverBg="#fef9c3"
                  hoverColor="#854d0e"
                  hoverIcon="✓"
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );

  return (
    <div style={{
      flex: 1, overflowY: "auto", background: "#f1f5f9",
      direction: "rtl", fontFamily: "inherit",
      padding: "0 0 40px",
    }}>

      {/* ── Header ── */}
      <div style={{
        background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
        padding: isMobile ? "14px 16px" : "20px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        color: "white",
      }}>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800 }}>שלום 👋</div>
          {!isMobile && <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>{todayLabel}</div>}
        </div>
        <div style={{ display: "flex", gap: 10, position: "relative" }}>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)",
              color: "white", padding: "7px 14px", borderRadius: 8,
              cursor: "pointer", fontWeight: 700, fontSize: 16,
              display: "flex", alignItems: "center", justifyContent: "center"
            }}
          >
            ☰
          </button>
          {mobileMenuOpen && (
            <div style={{
              position: "absolute", top: "100%", left: 0, marginTop: 8,
              background: "white", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              display: "flex", flexDirection: "column", gap: 4, padding: 8, zIndex: 50,
              minWidth: 160, border: "1px solid #e2e8f0"
            }}>
              {onOpenFilesManager && (
                <button onClick={() => { onOpenFilesManager(); setMobileMenuOpen(false); }} style={{ background: "transparent", border: "none", color: "#1e293b", padding: "10px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "right" }}>🗂️ קבצים</button>
              )}
              {onBulkSend && (
                <button onClick={() => { onBulkSend(); setMobileMenuOpen(false); }} style={{ background: "transparent", border: "none", color: "#1e293b", padding: "10px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "right" }}>📤 דוח חודשי לכולם</button>
              )}
              <button onClick={() => { setShowAgentProfile(true); setMobileMenuOpen(false); }} style={{ background: "transparent", border: "none", color: "#1e293b", padding: "10px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "right" }}>👤 פרטי סוכן</button>
              <button onClick={() => { setView(v => v === "settings" ? "home" : "settings"); setMobileMenuOpen(false); }} style={{ background: view === "settings" ? "#f1f5f9" : "transparent", border: "none", color: "#1e293b", padding: "10px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "right" }}>⚙️ הגדרות</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Settings screen ── */}
      {view === "settings" && (
        <SettingsScreen onBack={() => setView("home")} />
      )}

      <div style={{ display: view === "settings" ? "none" : undefined, padding: isMobile ? "12px 12px" : "20px 24px", flexDirection: "column", gap: isMobile ? 16 : 24, maxWidth: 1200, margin: "0 auto" }}>

        {/* ── Section A: Requires attention — desktop keeps it on top; on mobile
               it moves into the flex column below (order 2) ── */}
        {!isMobile && urgentSection()}

        {/* ── Sections B + C (+ A on mobile): side by side (desktop) /
               clients → urgent → activity (mobile) ── */}
        {(hasClients || recentActivity.length > 0 || (isMobile && urgentItems.length > 0)) && (
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 16 : 20, alignItems: isMobile ? "stretch" : "flex-start" }}>

            {isMobile && urgentSection({ order: 2 })}

            {/* ── Section B: My clients (table) ── */}
            {hasClients && (
              <section style={{ flex: 2, minWidth: 0, order: 1 }}>
                <div style={{ marginBottom: 10 }}>
                  {!isMobile && (
                    <SectionTitle style={{ margin: "0 0 8px" }}>👥 הלקוחות שלי ({clients.length})</SectionTitle>
                  )}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="🔍  חיפוש שם / ת.ז..."
                      style={{
                        fontSize: 12, padding: "6px 10px",
                        border: "1px solid #e2e8f0", borderRadius: 7,
                        outline: "none", direction: "rtl", textAlign: "right",
                        background: "white", width: isMobile ? "100%" : 200,
                      }}
                    />
                    {onSourceFilterChange && (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: isMobile ? 4 : 0 }}>
                        {SOURCE_FILTERS.map(f => {
                          const active = sourceFilter === f.key;
                          return (
                            <button
                              key={f.key}
                              onClick={() => onSourceFilterChange(f.key)}
                              style={{
                                fontSize: 11, padding: "5px 9px",
                                background: active ? "#4338ca" : "white",
                                color: active ? "white" : "#64748b",
                                border: "1px solid " + (active ? "#4338ca" : "#e2e8f0"),
                                borderRadius: 7, cursor: "pointer",
                                fontWeight: active ? 700 : 500, whiteSpace: "nowrap",
                              }}
                            >
                              {f.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {isMobile ? (
                  /* ── Mobile: compact single-line list ── */
                  <div style={{
                    background: "white", borderRadius: 12, border: "1px solid #e2e8f0",
                    overflow: "hidden", maxHeight: 320, overflowY: "auto",
                  }}>
                    {filteredClients.length === 0 ? (
                      <div style={{ padding: "20px", textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
                        לא נמצאו לקוחות 🔍
                      </div>
                    ) : filteredClients.map((client, i) => {
                      const name = client.fileInfo?.client || "לקוח";
                      const meta = clientMeta[name] || {};
                      const days = daysSince(meta.lastEntry?.date);
                      const daysColor = days === null ? "#94a3b8" : days > 20 ? "#dc2626" : days > 10 ? "#d97706" : "#059669";
                      const isLast = i === filteredClients.length - 1;
                      return (
                        <div key={client.clientId || client.id} onClick={() => onSelectClient(client.id)} style={{
                          padding: "9px 14px",
                          borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                          cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                        }}>
                          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 5 }}>
                            <span style={{
                              fontSize: 13, fontWeight: 700, color: "#1e293b",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>{name}</span>
                            {meta.openTasks > 0 && <span style={{ fontSize: 10, color: "#dc2626", flexShrink: 0 }}>🔔</span>}
                            {meta.unpaidFees > 0 && <span style={{ fontSize: 10, color: "#d97706", flexShrink: 0 }}>💰</span>}
                            {client.hasHoldings && <span style={{ fontSize: 9, flexShrink: 0 }}>📦</span>}
                            {client.hasAdvice   && <span style={{ fontSize: 9, flexShrink: 0 }}>📋</span>}
                          </div>
                          {days !== null && (
                            <span style={{ fontSize: 10, color: daysColor, flexShrink: 0 }}>{days}י׳</span>
                          )}
                          <span style={{ fontSize: 13, color: "#cbd5e1", flexShrink: 0 }}>›</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                /* ── Desktop: full table ── */
                <div style={{ background: "white", borderRadius: 12, border: "1px solid #e2e8f0", overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", direction: "rtl" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        {["שם לקוח", "ת.ז / מ\"ה", "מוצרים", "תכנון", "משפחה", "ימים ללא קשר", "תאריך קובץ", "הוסף פעולה", ""].map((h, idx) => (
                          <th key={idx} style={{
                            padding: "8px 12px", fontSize: 11, fontWeight: 700,
                            color: "#64748b", textAlign: "right", whiteSpace: "nowrap",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.length === 0 ? (
                        <tr>
                          <td colSpan={9} style={{ padding: "24px", textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
                            לא נמצאו לקוחות תואמים 🔍
                          </td>
                        </tr>
                      ) : filteredClients.map((client, i) => {
                        const name = client.fileInfo?.client || "לקוח";
                        const meta = clientMeta[name] || {};
                        const hasPlan = clientsWithPlans.has(client.clientId);
                        const lastDate = client.fileInfo?.taarichBitzua
                          || client.datasets?.[client.activeDatasetIndex]?.addedAt?.slice(0, 10);
                        const isLast = i === filteredClients.length - 1;
                        const days = daysSince(meta.lastEntry?.date);
                        const daysColor = days === null ? "#94a3b8"
                          : days > 20 ? "#dc2626"
                          : days > 10 ? "#d97706"
                          : "#059669";

                        return (
                          <tr
                            key={client.clientId || client.id}
                            onClick={() => onSelectClient(client.id)}
                            style={{
                              borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                              cursor: "pointer", transition: "background 0.1s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                            onMouseLeave={e => e.currentTarget.style.background = "white"}
                          >
                            <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                {name}
                                {client.hasHoldings && <span title="אחזקות" style={{ fontSize: 9 }}>📦</span>}
                                {client.hasAdvice && <span title="טרום-ייעוץ" style={{ fontSize: 9 }}>📋</span>}
                              </span>
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: 11, color: "#94a3b8" }}>
                              {client.fileInfo?.clientId || "—"}
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: 12, color: "#475569", textAlign: "center" }}>
                              {client.funds?.length || 0}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              {hasPlan ? <span style={{ fontSize: 12 }}>📐</span> : null}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              {allRelations[client.id]?.length > 0
                                ? <span style={{ fontSize: 11, fontWeight: 700, color: "#2563eb" }}
                                    title={allRelations[client.id]
                                      .map(r => clients.find(c => c.id === r.targetId)?.fileInfo?.client)
                                      .filter(Boolean).join(", ")}>
                                    👪 {allRelations[client.id].length}
                                  </span>
                                : null}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              {days !== null
                                ? <span style={{ fontSize: 11, fontWeight: 700, color: daysColor }}>{days}י׳</span>
                                : <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>}
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>
                              {lastDate ? fmtDate(lastDate) : "—"}
                            </td>
                            <td style={{ padding: "8px 6px", textAlign: "center" }}>
                              {onLogActivity && (
                                <button
                                  onClick={e => { e.stopPropagation(); setQuickLogClient(name); }}
                                  title="הוסף פעולה"
                                  style={{
                                    fontSize: 11, padding: "3px 7px",
                                    background: "#f1f5f9", border: "1px solid #e2e8f0",
                                    borderRadius: 5, cursor: "pointer", color: "#4338ca",
                                  }}
                                >➕</button>
                              )}
                            </td>
                            <td style={{ padding: "8px 6px", textAlign: "center" }}>
                              <span style={{ fontSize: 11, color: "#94a3b8" }}>›</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )} {/* end desktop/mobile branch */}
              </section>
            )}

            {/* ── Right Column: Consultations & Activity ── */}
            <div style={{ flex: 1, minWidth: isMobile ? 0 : 250, width: isMobile ? "100%" : undefined, display: "flex", flexDirection: "column", gap: 20, order: isMobile ? 3 : 2 }}>
              
              {/* ── Section C: Consultations in Process ── */}
              {activeProcessClients.length > 0 && (
                <section>
                  <SectionTitle>📐 ייעוצים בתהליך ({activeProcessClients.length})</SectionTitle>
                  <div style={{
                    background: "white", borderRadius: 12,
                    border: "1px solid #e2e8f0", overflow: "hidden",
                    maxHeight: isMobile ? 240 : 320, overflowY: "auto",
                  }}>
                    {activeProcessClients.map((client, i) => {
                      const name = client.fileInfo?.client || "לקוח";
                      const meta = clientMeta[name] || {};
                      const days = daysSince(meta.lastEntry?.date);
                      const daysColor = days === null ? "#94a3b8" : days > 20 ? "#dc2626" : days > 10 ? "#d97706" : "#059669";
                      const isLast = i === activeProcessClients.length - 1;
                      return (
                        <div key={client.clientId || client.id} onClick={() => onSelectClient(client.id)} style={{
                          padding: "10px 14px", borderBottom: isLast ? "none" : "1px solid #f1f5f9",
                          cursor: "pointer", display: "flex", alignItems: "center", gap: 10, transition: "background 0.1s"
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                        onMouseLeave={e => e.currentTarget.style.background = "white"}
                        >
                          <span style={{ fontSize: 16 }}>📐</span>
                          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {name}
                            </div>
                            <div style={{ fontSize: 11, color: "#64748b", display: "flex", gap: 8 }}>
                              {days !== null ? <span style={{ color: daysColor }}>ללא קשר: {days}י׳</span> : <span>טרם נוצר קשר</span>}
                              {meta.openTasks > 0 && <span style={{ color: "#dc2626" }}>משימות: {meta.openTasks}</span>}
                            </div>
                          </div>
                          <span style={{ fontSize: 13, color: "#cbd5e1" }}>›</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* ── Section D: Recent activity ── */}
              {recentActivity.length > 0 && (
                <section>
                  <SectionTitle>📋 פעילות אחרונה</SectionTitle>
                  <div style={{
                    background: "white", borderRadius: 12,
                    border: "1px solid #e2e8f0", overflow: "hidden",
                    maxHeight: isMobile ? 240 : (activeProcessClients.length > 0 ? 320 : 480), overflowY: "auto",
                  }}>
                  {recentActivity.map((entry, i) => {
                    const meta = TYPE_META[entry.type] || { label: "פעילות", icon: "📝", color: "#64748b" };
                    const entryClient = entry.clientName
                      ? clients.find(c => c.fileInfo?.client === entry.clientName)
                      : null;
                    return (
                      <div key={entry.id}
                        onClick={entryClient ? () => onSelectClient(entryClient.id) : undefined}
                        title={entryClient ? `פתח את ${entry.clientName}` : undefined}
                        onMouseEnter={e => { const b = e.currentTarget.querySelector(".del-btn"); if (b) b.style.opacity = "1"; if (entryClient) e.currentTarget.style.background = "#f8fafc"; }}
                        onMouseLeave={e => { const b = e.currentTarget.querySelector(".del-btn"); if (b) b.style.opacity = "0"; e.currentTarget.style.background = "transparent"; }}
                        style={{
                          display: "flex", alignItems: "flex-start", gap: 10,
                          padding: "10px 14px",
                          borderBottom: i < recentActivity.length - 1 ? "1px solid #f8fafc" : "none",
                          opacity: meta.auto ? 0.6 : 1,
                          cursor: entryClient ? "pointer" : "default",
                          transition: "background 0.1s",
                        }}>
                        <span style={{ fontSize: 15, flexShrink: 0, paddingTop: 1 }}>{meta.icon}</span>
                        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>{fmtDate(entry.date)}</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              {meta.auto && (
                                <span style={{ fontSize: 9, color: "#94a3b8", background: "#f1f5f9", padding: "1px 4px", borderRadius: 3 }}>אוטו</span>
                              )}
                              <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                            </div>
                          </div>
                          <div style={{
                            fontSize: 12, color: "#475569", textAlign: "right",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            fontWeight: 500,
                          }}>
                            {entry.description}
                          </div>
                          {entry.clientName && (
                            <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "right" }}>
                              👤 {entry.clientName}
                            </div>
                          )}
                          {entry.amount > 0 && entry.type === "fee" && (
                            <div style={{ fontSize: 11, color: "#d97706", fontWeight: 700, textAlign: "right", marginTop: 1 }}>
                              {Number(entry.amount).toLocaleString("he-IL")} ₪
                              {entry.feeCollected && <span style={{ color: "#16a34a", marginRight: 6 }}>✓ שולם</span>}
                            </div>
                          )}
                        </div>
                        {!meta.auto && (
                          <div className="del-btn" style={{ display: "flex", gap: 4, opacity: 0, transition: "opacity 0.2s" }}>
                            <button
                              onClick={e => { e.stopPropagation(); setEditEntry(entry); }}
                              title="ערוך פעולה"
                              style={{
                                background: "transparent", border: "none", cursor: "pointer",
                                fontSize: 13, padding: 4, color: "#94a3b8",
                              }}
                            >✏️</button>
                            {onDeleteLog && (
                              <button
                                onClick={e => { e.stopPropagation(); if (window.confirm("למחוק פעולה זו?")) onDeleteLog(entry.id); }}
                                title="מחק פעולה"
                                style={{
                                  background: "transparent", border: "none", cursor: "pointer",
                                  fontSize: 13, padding: 4, color: "#ef4444",
                                }}
                              >🗑️</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            </div> {/* Close Right Column */}

          </div>
        )}

        {/* ── Section D: No clients ── */}
        {!hasClients && (
          <section style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🏦</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", marginBottom: 8 }}>ברוכים הבאים</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>טען את הקובץ הראשון כדי להתחיל</div>
            <button
              onClick={onAddNew}
              style={{
                padding: "11px 28px", background: "#4338ca", color: "white",
                border: "none", borderRadius: 8, cursor: "pointer",
                fontWeight: 700, fontSize: 14,
              }}
            >+ טען לקוח</button>
          </section>
        )}
      </div>

      {/* ── Agent profile modal ── */}
      {showAgentProfile && (
        <AgentProfileModal
          clients={clients}
          logEntries={logEntries}
          onClose={() => setShowAgentProfile(false)}
        />
      )}

      {/* ── Quick log modal ── */}
      {quickLogClient && onLogActivity && (
        <QuickLogModal
          clientName={quickLogClient}
          onAdd={entry => { onLogActivity(entry); setQuickLogClient(null); }}
          onClose={() => setQuickLogClient(null)}
        />
      )}
      
      {/* ── Edit log modal ── */}
      {editEntry && (
        <QuickLogModal
          initialData={editEntry}
          onSave={(data) => {
            if (onUpdateLog) onUpdateLog(editEntry.id, data);
            setEditEntry(null);
          }}
          onClose={() => setEditEntry(null)}
        />
      )}
    </div>
  );
}

// ── Small helpers ────────────────────────────────────────────
function SectionTitle({ children, style = {} }) {
  return (
    <div style={{
      fontSize: 14, fontWeight: 800, color: "#1e293b",
      marginBottom: 10, textAlign: "right", ...style,
    }}>
      {children}
    </div>
  );
}

function ActionItemButton({ onClick, text, defaultIcon, hoverIcon, hoverBg, hoverColor }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        fontSize: 12, fontWeight: 600, padding: "6px 12px",
        background: isHovered ? hoverBg : "white",
        color: isHovered ? hoverColor : "#64748b",
        border: `1px solid ${isHovered ? hoverBg : "#cbd5e1"}`,
        borderRadius: 20, cursor: "pointer",
        flexShrink: 0, whiteSpace: "nowrap",
        display: "flex", alignItems: "center", gap: 6,
        transition: "all 0.2s ease",
      }}
    >
      <span style={{ fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isHovered ? hoverIcon : defaultIcon}
      </span>
      {text}
    </button>
  );
}
